import { isAdminRole, isSupplierRole, isSupporterRole, rolesQuery } from "@/api/roles";
import { supplierProfileQuery, supplierQuery } from "@/api/supplier";
import { supporterQuery } from "@/api/supporter";
import { auth, queryClient } from "@/api/utils";
import { useSupplierId, useSupporterId } from "@/utils/roles";
import { Avatar, Box, QueryHandler, Text, UnstyledButton } from "@givenwell/components";
import { CogIcon, CrownIcon, DotsIcon, HealthcareIcon, LogoutIcon, ShoppingBagIcon } from "@givenwell/core-solid-icons";
import { GetAssumableRolesResponse } from "@givenwell/management-api";
import { keyframes, styled } from "@givenwell/stitches";
import { colors, easing } from "@givenwell/theme";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { linkOptions, useMatches, useNavigate } from "@tanstack/react-router";
import { DropdownMenu } from "radix-ui";

export function AccountMenu({ role }: { role: "admin" | "supplier" | "supporter" }) {
  const urlSupplierId = useSupplierId();
  const urlSupporterId = useSupporterId();

  const { data: name } = useQuery(currentOrganisationNameQuery(role, urlSupplierId || urlSupporterId || ""));

  const { data: supplierProfile } = useQuery({ ...supplierProfileQuery(urlSupplierId!), enabled: !!urlSupplierId });

  return (
    <Box
      css={{
        pb: "max(var(--safe-area-inset-bottom), 16px)",
        width: "100%",
        backgroundColor: colors.warmGreyHalf,
      }}
    >
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <UnstyledButton
            css={{
              alignItems: "center",
              gap: 7,
              height: 40,
              minHeight: 40,
              pl: 8,
              width: "100%",

              textAlign: "left",
              display: "flex",
            }}
          >
            <Avatar
              src={supplierProfile?.profile_image_media?.url}
              givenName={name}
              css={{ backgroundColor: `${colors.gray} !important`, flexShrink: 0 }}
              size={24}
            />
            <Text
              css={{
                fontScale: "sm",
                fontWeight: 500,
                color: colors.text2,
                flex: "1 0 0px",
                overflow: "hidden",
                textWrap: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {name}
            </Text>
            <DotsIcon css={{ size: 14, flexShrink: 0 }} color={colors.text2} />
          </UnstyledButton>
        </DropdownMenu.Trigger>

        <DropdownMenuContent side="top" align="center" sideOffset={4} alignOffset={-8} collisionPadding={0}>
          <RolesSection />
          <DropdownMenuSeparator />
          <UserSection role={role} />
        </DropdownMenuContent>
      </DropdownMenu.Root>
    </Box>
  );
}

// <NavigationMenuA
//   href="mailto:support@givenwell.com?subject=Employer%20Support%20Request"
//   icon={<HelpQuestionIcon />}
// >
//   Help & Support
// </NavigationMenuA>

const currentOrganisationNameQuery = (role: "admin" | "supporter" | "supplier", id: string) =>
  queryOptions({
    queryKey: ["org-name", role, id],
    queryFn: async () => {
      if (role === "admin") {
        return "Givenwell";
      }
      if (role === "supplier") {
        const data = await queryClient.ensureQueryData(supplierQuery(id));
        return data.name;
      }
      if (role === "supporter") {
        const data = await queryClient.ensureQueryData(supporterQuery(id));
        return data.name;
      }
      return "Givenwell";
    },
  });

const open = keyframes({
  from: {
    opacity: 0,
    transform: "scale(0.95)",
  },
  to: {
    opacity: 1,
    transform: "scale(1)",
  },
});

const close = keyframes({
  from: {
    opacity: 1,
    transform: "scale(1)",
  },
  to: {
    opacity: 0,
    transform: "scale(0.95)",
  },
});

const DropdownMenuContent = styled(DropdownMenu.Content, {
  backgroundColor: colors.cream,
  borderRadius: 9 + 8,
  width: "fit-content",
  "@supports (width: calc-size(auto, size + 2rem))": {
    width: "calc-size(fit-content, size + 16px)",
  },
  minWidth: "calc(var(--radix-dropdown-menu-trigger-width) + 16px)",
  boxShadow: `0 0 0 1px ${colors.outline2}, 0 10px 40px rgba(0,0,0,0.1)`,
  padding: 8,
  transformOrigin: "var(--radix-dropdown-menu-content-transform-origin)",
  "&[data-state='open']": {
    animation: `${open()} 0.05s ${easing.standard}`,
  },
  "&[data-state='closed']": {
    animation: `${close()} 0.1s ${easing.standard}`,
  },
});

const DropdownMenuGroup = styled(DropdownMenu.Group, {});
const DropdownMenuLabel = styled(DropdownMenu.Label, {
  color: colors.text2,
  fontScale: "xs",
  weight: 500,
  px: 9,
  py: 8,
});
const DropdownMenuSeparator = styled(DropdownMenu.Separator, {
  my: 8,
  height: 1,
  mx: 9,
  backgroundColor: colors.outline2,
});

const DropdownMenuItemRoot = styled(DropdownMenu.Item, {
  height: 40,
  display: "flex",
  alignItems: "center",
  fontScale: "sm",
  weight: 500,
  px: 9,
  cursor: "default",

  "&[data-highlighted]": {
    backgroundColor: colors.warmGreyHalf,
    outline: "none",
    borderRadius: 9,
  },
});

function DropdownMenuItem({
  icon,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuItemRoot> & { icon?: React.ReactNode }) {
  return (
    <DropdownMenuItemRoot {...props}>
      <Box css={{ color: colors.text2, mr: 8 }}>{icon}</Box>
      {children}
    </DropdownMenuItemRoot>
  );
}

// ---

function RolesSection() {
  const roles = useQuery(rolesQuery);
  const urlSupplierId = useSupplierId();
  const urlSupporterId = useSupporterId();

  const navigate = useNavigate();
  const matches = useMatches();
  const isUsingAdmin = matches.some(match => match.routeId === "/admin");

  function handleSelect(role: GetAssumableRolesResponse["roles"][number]) {
    if (isAdminRole(role)) {
      navigate({
        to: "/admin",
      });
    }
    if (isSupplierRole(role)) {
      navigate({
        to: "/supplier/$supplierId",
        params: { supplierId: role.supplier_id },
      });
    }
    if (isSupporterRole(role)) {
      navigate({
        to: "/supporter/$supporterId",
        params: { supporterId: role.supporter_id },
      });
    }
  }

  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>Your Teams</DropdownMenuLabel>
      <QueryHandler
        query={roles}
        success={({ data }) => {
          return (
            <>
              {data.roles.map(role => (
                <RoleItem
                  key={isSupplierRole(role) ? role.supplier_id : isSupporterRole(role) ? role.supporter_id : "admin"}
                  role={role}
                  onSelect={() => handleSelect(role)}
                  active={
                    (isSupplierRole(role) && role.supplier_id === urlSupplierId) ||
                    (isSupporterRole(role) && role.supporter_id === urlSupporterId) ||
                    (role.$type === "admin" && isUsingAdmin)
                  }
                />
              ))}
            </>
          );
        }}
      />
    </DropdownMenuGroup>
  );
}

type RoleItemProps = { role: GetAssumableRolesResponse["roles"][number]; onSelect(): void; active?: boolean };
function RoleItem({ role, onSelect, active }: RoleItemProps) {
  const Icon = isSupplierRole(role) ? ShoppingBagIcon : isSupporterRole(role) ? HealthcareIcon : CrownIcon;
  return (
    <DropdownMenuItem onSelect={onSelect} icon={<Icon width="21" height="21" />}>
      <Box css={{}}>
        <Text css={{ lineClamp: 1 }}>{"name" in role ? role.name : "Givenwell Admin"}</Text>
      </Box>
      {/* <Center css={{ size: 24, ml: 16, color: colors.blue500 }}>
        {active && <CheckIcon width="14" height="14" />}
      </Center> */}
    </DropdownMenuItem>
  );
}

// ---

function UserSection({ role }: { role: "admin" | "supplier" | "supporter" }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const supplierId = useSupplierId();
  const supporterId = useSupporterId();

  async function handleLogout() {
    queryClient.clear();
    localStorage.removeItem("queryCache.roles.getAssumableRoles");
    auth.logout();
  }

  const userSettingsLinkOptions = {
    admin: linkOptions({
      to: "/admin/user-settings",
    }),
    supplier: linkOptions({
      to: "/supplier/$supplierId/user-settings",
      params: { supplierId: supplierId! },
    }),
    supporter: linkOptions({
      to: "/supporter/$supporterId/user-settings",
      params: { supporterId: supporterId! },
    }),
  }[role];

  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>Your Account</DropdownMenuLabel>
      <DropdownMenuItem icon={<CogIcon width="21px" height="21px" />} onClick={() => navigate(userSettingsLinkOptions)}>
        Account Settings
      </DropdownMenuItem>

      <DropdownMenuItem icon={<LogoutIcon width="21px" height="21px" />} onClick={handleLogout}>
        Log Out
      </DropdownMenuItem>
    </DropdownMenuGroup>
  );
}
